import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Star, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatarMoeda } from '@/lib/currency';
import { useOrdensServicoCliente } from '@/hooks/useClientesReais';

interface HistoricoServicosClienteProps {
  clienteId: string;
}

import type { TFunction } from 'i18next';
import { getStatusOSLabels } from '@/types/ordemServico';

// Status config sem 'excluida' - sistema usa hard delete
const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pendente: 'secondary',
  aceita: 'default',
  em_execucao: 'default',
  concluida: 'default',
  cancelada: 'destructive',
  recusada: 'destructive',
};

function getLocalStatusConfig(t: TFunction): Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> {
  const labels = getStatusOSLabels(t);
  const result: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {};
  for (const [key, variant] of Object.entries(STATUS_VARIANTS)) {
    result[key] = { label: labels[key as keyof typeof labels] || key, variant };
  }
  return result;
}

export function HistoricoServicosCliente({ clienteId }: HistoricoServicosClienteProps) {
  const { t } = useTranslation();
  const STATUS_CONFIG = getLocalStatusConfig(t);
  const navigate = useNavigate();
  const { data: ordens, isLoading, error } = useOrdensServicoCliente(clienteId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-destructive">
        {t('clients.history.errorLoadingServices')}
      </div>
    );
  }

  if (!ordens || ordens.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('clients.history.noServices')}
      </div>
    );
  }

  const renderEstrelas = (nota: number | null) => {
    if (!nota) return <span className="text-xs text-muted-foreground">{t('clients.detail.noEvaluation')}</span>;
    
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={cn(
              'h-3 w-3',
              star <= nota ? 'fill-primary text-primary' : 'text-muted-foreground'
            )}
          />
        ))}
        <span className="text-xs ml-1">{nota.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {ordens.map((os) => {
        const statusConfig = STATUS_CONFIG[os.status || 'pendente'] || STATUS_CONFIG.pendente;
        // Tipagem correta para servicos
        const servicos = Array.isArray(os.servicos)
          ? os.servicos.map((s: { descricao?: string; nome?: string }) => s.descricao || s.nome || 'Serviço').join(', ')
          : 'Serviço';

        return (
          <div
            key={os.id}
            className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
            onClick={() => navigate(`/ordens/${os.id}`)}
          >
            {/* Número da OS e Data */}
            <div className="min-w-[100px]">
              <p className="font-mono text-sm font-medium">{os.numero_os}</p>
              <p className="text-xs text-muted-foreground">
                {os.data_agendada 
                  ? format(new Date(os.data_agendada), 'dd/MM/yyyy', { locale: ptBR })
                  : t('orders.details.noDate')
                }
              </p>
            </div>

            {/* Serviços */}
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate">{servicos}</p>
              {os.prestador_nome && (
                <p className="text-xs text-muted-foreground truncate">
                  {t('orders.details.provider', { name: os.prestador_nome })}
                </p>
              )}
            </div>

            {/* Valor */}
            <div className="text-right min-w-[80px]">
              <p className="font-medium">{formatarMoeda(os.valor_total ?? 0)}</p>
            </div>

            {/* Status */}
            <Badge variant={statusConfig.variant} className="min-w-[90px] justify-center">
              {statusConfig.label}
            </Badge>

            {/* Avaliação */}
            <div className="min-w-[100px]">
              {renderEstrelas(os.nota_avaliacao)}
            </div>

            {/* Link */}
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
